/* eslint-disable quote-props */
import clsx from 'clsx';
import { sumBy } from 'lodash';
import { fromPairs } from 'lodash';
import { stubFalse } from 'lodash';
import { stubTrue } from 'lodash';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import { useState } from 'react';
import { useMemo } from 'react';
import React from 'react';
import ReactModal from 'react-modal';
import { Button } from '../../../base-components/Button';
import { Checkbox } from '../../../base-components/Checkbox/Checkbox';
import { ControlledCheckbox } from '../../../base-components/Checkbox/ControlledCheckbox';
import { Delimiter } from '../../../base-components/Delimiter';
import { Region } from '../../../types/entities';
import { ReferenceDistricts } from '../Filter/Filter.type';
import { ReferenceRegions } from '../Filter/Filter.type';
import { FacetBadgeLabel } from '../hooks/useMatchesLabel/FacetBadgeLabel';
import styles from './SelectRegionModal.module.scss';

export interface Props {
    regions: ReferenceRegions;
    districts: ReferenceDistricts;
    value: Region[];
    onChange: (value: Region[]) => void;
    onHide: () => void;
    matches?: { [k: string]: number };
}

export const SelectRegionModal: React.FC<Props> = ({
    regions,
    districts,
    onHide,
    value,
    onChange,
    matches,
}) => {
    const [districtCode, setDistrictCode] = useState(1);

    const [checkedRegions, setCheckedRegions] = useState({
        ...mapValues(keyBy(regions, 'regionCode'), stubFalse),
        ...mapValues(keyBy(value, 'regionCode'), stubTrue),
    });

    const specialRegions = useMemo(
        () => [
            {
                label: 'Москва и Подмосковье',
                fields: ['*'],
                regionCodes: ['77', '50'],
            },
            {
                regionCodes: ['78'],
                label: 'Санкт-Петербург',
            },
            {
                regionCodes: ['92'],
                label: 'Севастополь',
            },
        ],
        []
    );

    const filteredRegions = useMemo(
        () => filter(regions, ['federalDistrictCode', districtCode]),
        [regions, districtCode]
    );

    const [modalIsOpen, setIsOpen] = React.useState(true);

    const closeModal = () => {
        setIsOpen(false);
    };

    const handleChange = (regionCode: string, checked: boolean) => {
        setCheckedRegions({
            ...checkedRegions,
            [regionCode]: checked,
        });
    };

    const handleMultipleChange = (regionCodes: string[], checked: boolean) => {
        setCheckedRegions({
            ...checkedRegions,
            ...fromPairs(
                regionCodes.map((regionCode) => [regionCode, checked])
            ),
        });
    };

    const handleSubmit = () => {
        onChange(regions.filter((region) => checkedRegions[region.regionCode]));
        closeModal();
    };

    return (
        <ReactModal
            isOpen={modalIsOpen}
            onRequestClose={() => setIsOpen(false)}
            onAfterClose={onHide}
            closeTimeoutMS={200}
            contentLabel="modal"
            className={styles.modal}
            ariaHideApp={false}
        >
            <div className={styles.header}>
                <div className={styles.title}>Регионы России</div>
            </div>

            <div className={styles.body}>
                <div className={styles.specialRegions}>
                    {specialRegions.map(({ label, regionCodes }) => (
                        <ControlledCheckbox
                            key={label}
                            checked={regionCodes.every(
                                (regionCode) => checkedRegions[regionCode]
                            )}
                            onChange={(checked) =>
                                handleMultipleChange(regionCodes, checked)
                            }
                            className={styles.checkbox}
                        >
                            <FacetBadgeLabel
                                value={sumBy(
                                    regionCodes,
                                    (code) => matches?.[code] || 0
                                )}
                                hideZero
                            >
                                {label}
                            </FacetBadgeLabel>
                        </ControlledCheckbox>
                    ))}
                </div>
                <div className={styles.title}>Федеральный округ</div>
                <div className={styles.regions}>
                    <div className={styles.nav}>
                        {districts.map(({ code, name }) => (
                            <button
                                key={code}
                                type="button"
                                className={clsx(
                                    styles.btnLink,
                                    code === districtCode && styles.active
                                )}
                                onClick={() => setDistrictCode(code)}
                            >
                                {name}
                            </button>
                        ))}
                    </div>
                    <div className={styles.list}>
                        {filteredRegions.map(
                            ({ regionCode, offName, shortName }) => (
                                <ControlledCheckbox
                                    key={regionCode}
                                    checked={checkedRegions[regionCode]}
                                    onChange={(checked) =>
                                        handleChange(regionCode, checked)
                                    }
                                    className={styles.checkbox}
                                >
                                    <FacetBadgeLabel
                                        value={matches?.[regionCode] || 0}
                                        hideZero
                                    >
                                        {`${offName} ${shortName}`}
                                    </FacetBadgeLabel>
                                </ControlledCheckbox>
                            )
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.footer}>
                <Button
                    variant="secondary"
                    className={styles.button}
                    onClick={closeModal}
                >
                    Отменить
                </Button>
                <Delimiter />
                <Button
                    variant="primary"
                    className={styles.button}
                    onClick={handleSubmit}
                >
                    Выбрать
                </Button>
            </div>
        </ReactModal>
    );
};
